// 存钱罐主UI
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import BottleBg from './images/bg-bottle.png';
import Image from 'next/image';
import SettingGroup from "./components/setting-group";
import { Button } from "@/components/ui/button";
import { useBalanceFormatter } from "@/hooks/use-balance-formatter";
import BgPrivilegeShadow from './images/bg-privilege-shadow.png';
import PrivilegeContent from './components/privilege-content';
import IconVipUp from './images/icon-vip-up.png';
import IconDecorate from './images/icon-decorate.png';
import TaskList from "./components/task-list";
import GetAnimation from "./components/get-animation";
import { gullakBalanceAtom, gullakTaskListAtom, gullakTaskLoadingAtom, gullakVipRulesAtom, gullakVipRulesLoadingAtom, useGullakBalanceManager, userInfoAtom } from "@/store";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import styles from './index.module.css';
import clsx from "clsx";
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import LottieView from "@/utils/lottie-helper";
import { claimGullakCash, getGullakVipRules } from "./services";
import { Spin } from "@/components/ui-feat/spin";
import {
  canClaim,
  isAboveDailyLimit,
  getNextClaimCountdown,
  isAlreadyClaimed,
  isLevelInsufficient,
  isAmountInsufficient,
  isGullakOpen,
  checkCanTranslate,
  checkIsSpecialRate,
} from "./helpers";
import { useCountdown } from "@/hooks/use-countdown";
import { useTranslations } from "next-intl";
import { useGullakLogin } from "./hooks/use-gullak-login";
import { useSpecialRateCountdown } from "./hooks/use-special-rate-countdown";
import { hideActivityOfflineNoticeModal, showActivityOfflineNoticeModal } from "@/components/ui-feat/activityOfflineNoticeModal";
import type { ClaimRes } from "@/types/api/gullak-cash/claim";
import { hideGullakCashDrawer, showRuleDrawer } from "./modal";
import { getCurrency } from "@/utils/currency";
import { track } from "@/utils/track";
import { add, sub, toDP } from "@/libs/decimal";
import { isNil } from "lodash-es";
import staticAnimation from './lottie/static.json';
import useFn from "@/hooks/use-fn";
import BgYellowTip from './images/bg-translate-tip.png';
import useGullakHomeData from "./hooks/use-gullak-home-data";
import { LOGIN_SOURCE_MAP } from "@/const/report";
import { getGullakDrawerCache, setGullakDrawerCache } from "./cache";
import TruncatedCountUp from "./components/truncated-count-up";
import { MIN_CLAIMABLE_CASH } from "./constants";
import toast from "react-hot-toast";

const ClaimAnimation = lazy(() => import('./components/claim-animation'));

export interface GullakCashDrawerProps {
  source?: string;
}

interface GullakCashAnimationQueueItem {
  balanceDiff: number | string; // 增加的金额，可能是数字或者'<0.01'的字符串
  previousGullakCash: number;
  endGullakCash: number;
}

type ClaimBlockReason = 'loading' | 'claimed' | 'vip' | 'amount' | null;

const GullakCashDrawer = NiceModal.create((props: GullakCashDrawerProps) => {
  const { source } = props;
  // 存钱罐余额
  const gullakBalance = useAtomValue(gullakBalanceAtom);
  // 存钱罐主页数据
  const { gullakHomeData, updateGullakHomeData } = useGullakHomeData();
  // VIP规则数据和加载状态
  const [gullakVipRules, setGullakVipRules] = useAtom(gullakVipRulesAtom);
  const setGullakVipRulesLoading = useSetAtom(gullakVipRulesLoadingAtom);
  // 用户信息
  const userInfo = useAtomValue(userInfoAtom);
  const { isLogin, handleShowLoginModal } = useGullakLogin();
  const modal = useModal();
  const { formatCurrency } = useBalanceFormatter();
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<ClaimRes | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // 动画显示状态
  const [showClaimAnimation, setShowClaimAnimation] = useState(false);
  const [showGetAnimation, setShowGetAnimation] = useState(false);
  const [balanceDiff, setBalanceDiff] = useState<number | string>(0); // 余额差值
  const isAnimating = useRef(false); // 串行消费动画队列
  const animationQueueRef = useRef<GullakCashAnimationQueueItem[]>([]);
  const currentAnimationRef = useRef<GullakCashAnimationQueueItem | null>(null);

  // gullak_cash CountUp 相关状态
  const [previousGullakCash, setPreviousGullakCash] = useState<number>(0);
  const [endGullakCash, setEndGullakCash] = useState<number>(0);

  // 多语言
  const t = useTranslations('auth');
  const t_gullak = useTranslations('gullak');
  const t_reward = useTranslations('reward');

  const { displayText: claim_countdown, startCountDown: startClaimCountdown, stopCountDown: stopClaimCountdown } = useCountdown();
  const { displayText: rate_countdown_str, displaySeconds,
    startCountDown: startTranslateCountdown, stopCountDown: _stopTranslateCountdown } =
    useCountdown({ size: 'min', showSecondOverDay: true });

  const { updateGullakBalance } = useGullakBalanceManager();
  const isOpen = gullakBalance || gullakHomeData ? isGullakOpen(gullakBalance ? gullakBalance?.status : gullakHomeData?.status) : undefined;
  // 是否开启转化
  const canTranslate = isNil(gullakHomeData?.vip_switch_status) ? true : checkCanTranslate(gullakHomeData.vip_switch_status);
  // 是特殊转化率期间
  const isSpecialRate =
    isNil(gullakHomeData?.special_status) ||
      !canTranslate
      ? false : checkIsSpecialRate(gullakHomeData.special_status);
  // 是否已达到特殊转化的最高流水限制
  const isExceedValidFlow = isSpecialRate && Number(gullakHomeData?.special_daily_flow) >= Number(gullakHomeData?.special_vaild_flow);

  // 监听数据返回，更新初始加载状态
  useEffect(() => {
    if (!isNil(gullakHomeData)) {
      setIsInitialLoading(false);
    }
  }, [gullakHomeData]);

  // 监听弹窗打开状态，预加载VIP规则数据
  useEffect(() => {
    if (modal.visible) {
      // 异步加载VIP规则数据
      (async () => {
        try {
          setGullakVipRulesLoading(true);
          const vipRulesData = await getGullakVipRules();
          setGullakVipRules(vipRulesData);
        } catch (error) {
          console.error('Failed to fetch VIP rules:', error);
        } finally {
          setGullakVipRulesLoading(false);
        }
      })();
    }
  }, [modal.visible, setGullakVipRules, setGullakVipRulesLoading]);

  useEffect(() => {
    if (modal.visible) {
      track('gullak_page_view', { source });
      // 监听弹窗打开状态或 gullakBalance.balance 变化，刷新主页数据
      updateGullakHomeData().then((data) => {
        onClaimCountdown(data);
        onGullakcashChange(data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.visible, gullakBalance?.balance, source]);

  // 特殊转化率倒计时调度：参数变化时自动重新调度
  useSpecialRateCountdown({
    isSpecialRate,
    special_start_at: gullakHomeData?.special_start_at,
    special_end_at: gullakHomeData?.special_end_at,
    onStartCountdown: (remaining, onEnd) => startTranslateCountdown(remaining, onEnd),
    onEndCountdown: () => updateGullakHomeData(),
  });

  // 活动下架显示弹窗
  useEffect(() => {
    if (modal.visible && !isInitialLoading && !isNil(isOpen) && !isOpen) {
      showActivityOfflineNoticeModal({
        btn: {
          text: t_gullak('back'),
          onClick: () => {
            hideActivityOfflineNoticeModal();
            hideGullakCashDrawer();
          }
        }
      });
    }
  }, [isOpen, isInitialLoading, modal.visible]);

  // 点击登录并埋点
  const handleShowLoginWithTrack = () => {
    track('register_login_entrance_click', {
      current_module: LOGIN_SOURCE_MAP.Gullak,
    });
    handleShowLoginModal();
  };

  const onClaimCountdown = (data: typeof gullakHomeData) => {
    if (data && isAlreadyClaimed(data.is_claim)) {
      const secondsUntilMidnight = getNextClaimCountdown(data?.countdown || 0);
      startClaimCountdown(secondsUntilMidnight, () => {
        // 倒计时结束，重新获取数据
        updateGullakHomeData();
      });
    }
  };

  const consumeAnimationQueue = useFn(() => {
    if (isAnimating.current) return;

    const nextAnimation = animationQueueRef.current.shift();
    if (!nextAnimation) return;

    currentAnimationRef.current = nextAnimation;
    isAnimating.current = true;
    setPreviousGullakCash(nextAnimation.previousGullakCash);
    setEndGullakCash(nextAnimation.previousGullakCash);
    setBalanceDiff(nextAnimation.balanceDiff);
    setShowGetAnimation(true);
  });

  const onGullakcashChange = (data: typeof gullakHomeData) => {
    if (!modal.visible || !userInfo?.userId || !isLogin || !data?.gullak_cash) return;
    // 最新A+B的值
    const currentBalance = add(Number(data?.gullak_cash || 0), Number(data?.deposit_cash));
    // 如果和头部存钱罐的数值gullakBalance?.balance不相等（因为头部数值是轮询的，而homeData是最新的），则手动更新
    if (currentBalance !== Number(gullakBalance?.balance)) {
      updateGullakBalance();
    }
    // 读取缓存
    const { balance: cachedBalanceValue, gullakCash: cachedGullakcashValue } = getGullakDrawerCache(userInfo.userId);
    // 如果没有缓存，初始化缓存
    if (cachedBalanceValue === null) {
      setGullakDrawerCache(userInfo.userId, {
        balance: currentBalance,
        gullakCash: Number(data?.gullak_cash),
      });
      setPreviousGullakCash(Number(data?.gullak_cash));
      setEndGullakCash(Number(data?.gullak_cash));
      return;
    }
    if (!currentBalance) return;
    const previousBalance = cachedBalanceValue;
    const previousCash = cachedGullakcashValue ?? 0;
    // 如果有缓存值且当前值>缓存值，说明 balance 增加了
    if (currentBalance > previousBalance) {
      const balanceDiff = sub(currentBalance, previousBalance);
      const endGullakCash = Number(data?.gullak_cash);

      animationQueueRef.current.push({
        balanceDiff: balanceDiff < 0.01 ? '<0.01' : toDP(balanceDiff, 2),
        previousGullakCash: previousCash,
        endGullakCash,
      });
      consumeAnimationQueue();
    } else if (!isAnimating.current && animationQueueRef.current.length === 0) {
      setPreviousGullakCash(Number(data?.gullak_cash));
      setEndGullakCash(Number(data?.gullak_cash));
    }
    // 更新缓存值
    setGullakDrawerCache(userInfo.userId, {
      balance: currentBalance,
      gullakCash: Number(data?.gullak_cash),
    });
  };

  const handleClose = () => {
    if (showGetAnimation || showClaimAnimation) {
      return;
    }
    // 清除所有动画相关的状态
    setShowClaimAnimation(false);
    setClaimResult(null);
    // 停止倒计时
    stopClaimCountdown();
    modal.hide();
  };

  // 点击 Claim 按钮
  const handleClaim = async () => {
    if (gullakHomeData && (!canClaim(gullakHomeData, isLogin) || isClaiming)) {
      return;
    }
    track('gullak_page_button_click', { button_name: 'Claim' });
    try {
      setIsClaiming(true);
      // 调用领取接口
      const result = await claimGullakCash();
      // 保存领取结果，用于显示动效
      setClaimResult(result);
      // 显示动画层
      setShowClaimAnimation(true);
      // 更新gullakcash余额
      updateGullakBalance();
      // 领取成功后重新获取存钱罐主页数据
      const newData = await updateGullakHomeData();
      onClaimCountdown(newData);
    } catch (error) {
      console.error('Claim failed:', error);
      // 这里可以添加错误提示
    } finally {
      setIsClaiming(false);
    }
  };

  const getClaimBlockReason = (): ClaimBlockReason => {
    if (!isLogin || !gullakHomeData) return null;
    if (isClaiming) return 'loading';
    if (claim_countdown) return 'claimed';
    if (isLevelInsufficient(gullakHomeData.vip_level)) return 'vip';
    if (isAmountInsufficient(gullakHomeData.deposit_cash)) return 'amount';
    if (isAlreadyClaimed(gullakHomeData.is_claim)) return 'claimed';

    return null;
  };

  const handleClaimButtonClick = () => {
    const blockReason = getClaimBlockReason();
    const blockedToastId = 'gullak-claim-blocked';

    if (blockReason === 'loading') {
      return;
    }

    if (blockReason === 'vip') {
      const points = toDP(sub(gullakHomeData?.next_vip_min_points || 0, gullakHomeData?.user_points || 0), 2);
      toast(t_gullak('pointsToVip1', { points: points < 0.01 ? '<0.01' : points }), { id: blockedToastId });
      return;
    }

    if (blockReason === 'claimed') {
      toast(t_gullak('canClaimOncePerDay'), { id: blockedToastId });
      return;
    }

    if (blockReason === 'amount') {
      toast(t_gullak('betToEarnMoreCash'), { id: blockedToastId });
      return;
    }

    handleClaim();
  };

  const handleShowRule = () => {
    const minRate = gullakVipRules?.vip_gullak_rate ?
      [...gullakVipRules.vip_gullak_rate].sort((pre, next) => Number(pre.vip_rate) - Number(next.vip_rate))[0]?.vip_rate : 0;
    showRuleDrawer(canTranslate ? {
      specialRate: isSpecialRate ? {
        rate: Number(gullakHomeData?.special_vip_rate),
        // 判断是否登录, 未登录取最小的转化值
        normalRate: isLogin ? Number(gullakHomeData?.vip_rate || '') : Number(minRate),
        currency: gullakHomeData?.currency || 'INR',
        countdown: displaySeconds,
        translate_turnover: isLogin ? Number(gullakHomeData?.special_daily_flow) : 0,
        total_turnover: Number(gullakHomeData?.special_vaild_flow),
      } : undefined
    } : {});
  };

  const onTriggerCountUp = useFn(() => {
    const currentAnimation = currentAnimationRef.current;
    if (!currentAnimation) return;

    setEndGullakCash(currentAnimation.endGullakCash);
  });

  // 获取任务列表状态
  const taskList = useAtomValue(gullakTaskListAtom);
  const taskLoading = useAtomValue(gullakTaskLoadingAtom);

  // 判断是否有任务数据（不在loading中且有数据）
  const hasTaskData = !taskLoading && taskList.length > 0;

  // 动态计算 drawer 高度
  const drawerHeight = taskLoading || hasTaskData ? '84.19vh' : '66.5vh';

  const isBelowVipLevel = gullakHomeData?.vip_level !== undefined && isLevelInsufficient(gullakHomeData.vip_level);
  const isBelowAmount = gullakHomeData?.deposit_cash !== undefined && isAmountInsufficient(gullakHomeData.deposit_cash);
  const showCumulativeAmount = isLogin && !isBelowVipLevel;
  const currencySymbol = getCurrency(gullakHomeData?.currency || 'INR');
  const claimBlockReason = getClaimBlockReason();
  const isClaimButtonDisabledStyle = isLogin && !!gullakHomeData && (claimBlockReason !== null || isClaiming);
  // 当gullakcash为0时提示
  const showTranslateTip = isLogin && !isNil(gullakHomeData?.gullak_cash) && !Number(gullakHomeData.gullak_cash);

  const btnText = !isLogin
    ? t('loginSignup')
    : isBelowVipLevel
      ? t_gullak('reachVipToClaim')
      : claim_countdown
        ? claim_countdown
        : isBelowAmount
          ? t_gullak('reachAmountToClaim', { amount: `${currencySymbol}${MIN_CLAIMABLE_CASH}` })
          : isAlreadyClaimed(gullakHomeData?.is_claim)
            ? t_reward('claimed')
            : t_reward('claim');

  const renderCountUp = ({ className }: { className: string; }) => {
    return (
      <TruncatedCountUp
        start={previousGullakCash}
        end={endGullakCash}
        duration={2}
        preserveValue={true}
        prefix={currencySymbol}
        className={`${className}`}
        onEnd={() => {
          setPreviousGullakCash(endGullakCash);
        }}
      />
    );
  };

  return (
    <>
      <Drawer open={modal.visible} onOpenChange={handleClose}>
        <DrawerContent className="flex rounded-t-3xl" style={{ height: drawerHeight }}>
          <VisuallyHidden.Root>
            <DrawerTitle></DrawerTitle>
          </VisuallyHidden.Root>
          <div className={`relative h-full overflow-hidden overflow-y-auto no-scrollbar rounded-t-3xl bg-[#181D29]`}>
            <DrawerClose asChild>
              <Image
                src="/assets/images/ic_close.png"
                className='absolute right-4 top-[7.8vw] z-30 p-1 active:scale-90'
                alt="close"
                width={20}
                height={20}
              />
            </DrawerClose>
            <SettingGroup handleShowRuleDrawer={handleShowRule} />
            {isInitialLoading ? (
              <div className={`rounded-t-3xl h-full w-full flex items-center justify-center ${styles.bgLinear}`}>
                <Spin />
              </div>
            ) : (
              <>
                <div className="relative">
                  <Image
                    className={`absolute top-[60.29vw] w-full h-[11.7vw]`}
                    src={IconDecorate.src}
                    alt="decorate"
                    width={768}
                    height={90}
                    priority
                    style={{ width: '100%', height: '11.7vw' }}
                  />
                  {/* 罐子 */}
                  <div className="relative">
                    <Image
                      src={BottleBg.src}
                      alt="bottle-bg"
                      width={768}
                      height={806}
                      priority
                      className={styles.bottleBackground}
                    />
                    <LottieView<'canvas'>
                      animationData={staticAnimation}
                      className="statis-lottie absolute left-0 right-0 top-0"
                      loop
                      autoplay
                      renderer="canvas"
                      style={{ width: '100vw', height: 'auto', aspectRatio: '768/806' }}
                    />
                    <div className="absolute top-[44vw] left-[50%] -translate-x-1/2 text-[20px]">
                      <div className={`${styles.gullakCashLinear1}`}>
                        {t_gullak('gullakCash')}
                      </div>
                      <div className={`${styles.gullakCashLinear2} ${!isLogin && styles.gullakCashLinearUnLogin}`}>
                        {t_gullak('gullakCash')}
                      </div>
                    </div>
                    {showTranslateTip && (
                      <div className="absolute flex items-center justify-center w-[40vw] rotate-[-3deg] top-[35vw] right-[4.4vw]"
                        style={{ backgroundImage: `url(${BgYellowTip.src})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' }}
                      >
                        <div className={`${styles.yellowTipTextLinear} py-1 px-2 pl-3 pb-3 text-[2.1vw] font-semibold whitespace-break-spaces`}>
                          {t_gullak('obtainCashByTask')}
                        </div>
                      </div>
                    )}
                    {/* 存钱罐金额金额 */}
                    <div className={clsx(`absolute top-[51vw] left-[50%] -translate-x-1/2 font-bold`, isLogin ? 'text-2xl' : 'text-3xl')}>
                      <div className="relative">
                        {!isLogin ? <>
                          <div className={`${styles.amountGradient1}`}>? ? ?</div>
                          <div className={`${styles.amountGradient2}`}>? ? ?</div>
                        </> : (
                          <>
                            {renderCountUp({ className: styles.amountGradient1 })}
                            {renderCountUp({ className: styles.amountGradient2 })}
                          </>
                        )
                        }
                      </div>
                    </div>

                    {/* 可领取收益金额 */}
                    <div className="flex items-center absolute top-[81vw] left-[50%] -translate-x-1/2" /* onClick={handleClaimMock} */>
                      {isLogin ? (
                        <>
                          {/* <Image src={IconCash.src} alt="cash-title" width={44} height={14} /> */}
                          <div className={`relative text-[15px] h-5 flex items-center`} style={{ width: t_gullak('cash').length * 3 + 'vw' }}>
                            <div className={`${styles.cashLabel1} absolute top-0 w-fit`}>{t_gullak('cash')}：</div>
                            <div className={`${styles.cashLabel2} absolute top-0 w-fit`}>{t_gullak('cash')}：</div>
                          </div>
                          <div className={`${styles.cashAmount} text-[15px] h-5 font-semibold`}>
                            {formatCurrency(gullakHomeData?.deposit_cash || 0, gullakHomeData?.currency || 'INR')}
                          </div>
                        </>
                      ) : <div className="text-[2.86vw] font-medium mt-[3px]">
                        {t_gullak('loginToViewGullakCash')}&nbsp;&nbsp;
                      </div>}

                    </div>

                    <div className={`${styles.bottomButtonContainer} w-full text-center`}>
                      {/* 每自然日最多可领取1次,单次最大领取xxx法币(后台配置)，当地时间0点刷新可领取次数 */}
                      {/* 未登录显示登录按钮、已登录显示：可领取claim、未达等级（vip等级<1）不可领取、领取收益<1法币不可领取、倒计时（已领取则显示倒计时） */}
                      <Button
                        variant={isLogin ? 'success' : 'primary'}
                        className={clsx(
                          'w-[68.75vw] h-[9.4vw] rounded-lg !text-[13px]',
                          isClaimButtonDisabledStyle && 'opacity-35 active:scale-100'
                        )}
                        loading={isClaiming}
                        onClick={!isLogin ? handleShowLoginWithTrack : handleClaimButtonClick}
                      >
                        {btnText}
                      </Button>
                      {/* 当可领取的金额大于xxx法币的时候才展示 */}
                      {isAboveDailyLimit(gullakHomeData?.deposit_cash, gullakHomeData?.max_claim_amount || '') && (
                        <div className="absolute rotate-[-3deg] bottom-[6.5vw] right-[2.86vw] w-[36vw] leading-3"
                          style={{ backgroundImage: `url(${BgYellowTip.src})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' }}
                        >
                          <div className={`${styles.yellowTipTextLinear} p-1 pb-2 text-[9px] font-semibold`}>
                            {t_gullak('dailyClaimLimit', { amount: formatCurrency(gullakHomeData?.max_claim_amount || '', gullakHomeData?.currency || 'INR') })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`flex flex-col relative text-center h-[27.6vw] ${styles.privilegeContainer}`}
                  >
                    {hasTaskData && <Image src={BgPrivilegeShadow.src} alt="bg-privilege-shadow" width={768} height={212} className="absolute bottom-[-8px] left-0 w-full h-auto" style={{ height: '100%' }} />}
                    {isBelowVipLevel && (
                      <div className="mx-auto w-fit flex items-center">
                        <Image src={IconVipUp.src} alt="vip-up" width={25} height={11} className="mr-1" />
                        <div className="text-tertiary text-[10px] text-center">
                          {t_gullak('depositToUpVip')}
                        </div>
                      </div>
                    )}
                    {/* 累计领取金额，当【未达等级（vip等级<1）不可领取 】不展示 */}
                    {showCumulativeAmount && (
                      <div className="text-tertiary text-[10px] text-center">
                        {t_gullak('totalClaimed')}: <span className={styles.cumulativeAmount}>
                          {gullakHomeData?.total_cash && formatCurrency(gullakHomeData.total_cash, gullakHomeData.currency || 'INR')}
                        </span>
                      </div>
                    )}
                    <PrivilegeContent
                      specialRate={isSpecialRate && !isExceedValidFlow ? Number(gullakHomeData?.special_vip_rate) : null}
                      rateCountdown={rate_countdown_str}
                      turnover={Number(gullakHomeData?.special_vaild_flow)}
                      canTranslate={canTranslate}
                      isLogin={isLogin}
                      vipRate={gullakHomeData?.vip_rate}
                      vipMaxRate={gullakHomeData?.vip_max_rate}
                      handleShowRule={handleShowRule}
                    />
                  </div>
                </div>
                <TaskList setHideModal={handleClose} />
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Claim cash 动效 */}
      <Suspense fallback={null}>
        {isLogin ? <ClaimAnimation
          visible={showClaimAnimation}
          claimResult={claimResult}
          currency={gullakHomeData?.currency}
          onComplete={() => {
            setShowClaimAnimation(false);
            setClaimResult(null);
          }}
        /> : null}
      </Suspense>

      {/* Get Gullak 动效 */}
      {isLogin ? <GetAnimation
        visible={showGetAnimation}
        balanceDiff={balanceDiff}
        currency={gullakHomeData?.currency}
        onComplete={() => {
          const currentAnimation = currentAnimationRef.current;
          isAnimating.current = false;
          setShowGetAnimation(false);
          setBalanceDiff(0);
          if (currentAnimation) {
            setPreviousGullakCash(currentAnimation.endGullakCash);
            setEndGullakCash(currentAnimation.endGullakCash);
          }
          currentAnimationRef.current = null;
          setTimeout(() => {
            consumeAnimationQueue();
          }, 300);
        }}
        onTriggerCountUp={onTriggerCountUp}
      /> : null}
    </>
  );
});

export default GullakCashDrawer;
